import { select, take, put, takeEvery } from "redux-saga/effects";
import Logger from "../utils/logger";
import {
  isRestMiniGWPEnabled,
  getAutoApplyDiscountCode,
} from "../utils/promos";
import {
  constructCtaProductData,
  getLineItemDataByVariantId,
} from "../utils/shop";
import {
  addToCartRequest,
  updateQuantityRequest,
  removeDiscountCodeRequest,
  addProductAsGiftWithPurchaseRequest,
  removeProductAsGiftWithPurchaseRequest,
  removeProductAsGiftWithPurchaseReceive,
} from "../actions/ShopActions";
import {
  getCheckoutId,
  getCartLineItems,
  getCheckoutState,
  getAllProducts,
  getGiftWithPurchaseProducts,
} from "../utils/reduxSelectors";

// Events
import * as Shop from "../types/Shop";

// Constants
import { REST_KEY, REST_MINI_KEY } from "../utils/productConstants";
const TAG = "RestMiniGiftWithPurchaseSaga";
const AUTO_APPLY_DISCOUNT_CODE = getAutoApplyDiscountCode();
const IS_REST_MINI_PROMO_ENABLED = isRestMiniGWPEnabled();

const RestMiniGiftWithPurchaseSaga = () => {
  function* worker(action) {
    Logger.d(TAG, `Processing action: ${action.type}`);

    const checkoutId = yield select(getCheckoutId) || null;
    /**
     *  GIFT WITH PURCHASE
     * ***************************************************
     */
    const allProducts = yield select(getAllProducts);
    const restMiniData = allProducts[REST_MINI_KEY];
    const restData = allProducts[REST_KEY];
    const isRestProduct = allProducts[REST_KEY]
      ? allProducts[REST_KEY].variants[0].id === action.variantId
      : false;
    const isRestMiniProduct = allProducts[REST_MINI_KEY]
      ? allProducts[REST_MINI_KEY].variants[0].id === action.variantId
      : false;

    // When a product is added to cart, check to see
    if (
      action.type === Shop.ADD_TO_CART_RECEIVE ||
      action.type === Shop.UPDATE_QUANTITY_RECEIVE
    ) {
      const allGWPProducts = yield select(getGiftWithPurchaseProducts);
      const discountCode = AUTO_APPLY_DISCOUNT_CODE || null;

      if (
        IS_REST_MINI_PROMO_ENABLED &&
        discountCode &&
        (allGWPProducts[REST_MINI_KEY] < 2 || !allGWPProducts[REST_MINI_KEY])
      ) {
        // RestMini is in the cart and no GWP one is present, just apply the discount
        const cartLineItems = yield select(getCartLineItems);
        const restMiniLineItem = getLineItemDataByVariantId(
          cartLineItems,
          restMiniData.variants[0].id
        );
        const restLineItem = getLineItemDataByVariantId(
          cartLineItems,
          restData.variants[0].id
        );
        let restMiniAddable = false;

        if (restLineItem) {
          if (!restMiniLineItem && restLineItem.quantity === 1) {
            restMiniAddable = true;
          } else if (
            restMiniLineItem.quantity < 2 &&
            restLineItem.quantity === 2
          ) {
            restMiniAddable = true;
          }
        }

        if (restMiniAddable) {
          const productData = constructCtaProductData(
            restMiniData,
            restMiniData.variantId
          );

          yield put(
            addToCartRequest({
              checkoutId,
              quantity: 1,
              variantId: productData.variantId,
              customAttributes: productData.customAttributes,
            })
          );

          const addFreeRestMiniToCart = yield take([
            Shop.ADD_TO_CART_RECEIVE,
            Shop.ADD_TO_CART_REQUEST_FAILURE,
          ]);

          if (addFreeRestMiniToCart.type === Shop.ADD_TO_CART_REQUEST_FAILURE) {
            return Logger.d(
              TAG,
              `Add free rest to cart error: ${JSON.stringify(
                addFreeRestMiniToCart.errors
              )}`
            );
          }
        }

        yield put(
          addProductAsGiftWithPurchaseRequest({
            checkoutId,
            discountCode,
            productKey: REST_MINI_KEY,
          })
        );

        const applyFreeRestMiniDiscountCodeRequest = yield take([
          Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
          Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE,
        ]);

        if (
          applyFreeRestMiniDiscountCodeRequest.type ===
          Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Add free rest discount code error: ${JSON.stringify(
              applyFreeRestMiniDiscountCodeRequest.errors
            )}`
          );
        }

        return Logger.d(TAG, "Free rest added successfully to cart");
      }
    }

    // When FREE RestMini is removed from cart
    if (action.type === Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST) {
      const isRestMiniProduct =
        restMiniData.variants[0].id === action.lineItem.variantId;

      if (isRestMiniProduct) {
        // First trigger removal of discount code
        yield put(
          removeDiscountCodeRequest(
            action.checkoutId,
            REST_MINI_KEY,
            action.lineItem.variantId
          )
        );

        const removeRestMiniGWPDiscountRequest = yield take([
          Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
          Shop.REMOVE_DISCOUNT_CODE_FAILURE,
        ]);

        if (
          removeRestMiniGWPDiscountRequest.type ===
          Shop.REMOVE_DISCOUNT_CODE_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Removing free rest discount code error: ${JSON.stringify(
              removeRestMiniGWPDiscountRequest.errors
            )}`
          );
        }

        return Logger.d(TAG, `Removing discount code success!`);
      }
    }

    if (
      action.type === Shop.REMOVE_DISCOUNT_CODE_RECEIVE &&
      action.discountCode === REST_MINI_KEY
    ) {
      const cartLineItems = yield select(getCartLineItems);
      const lineItem = getLineItemDataByVariantId(
        cartLineItems,
        action.variantId
      );

      // Then update the rest quantity
      // console.log('REMOVING FREE RestMini!!!', action)
      // Update quantity of line item
      yield put(
        updateQuantityRequest(checkoutId, lineItem.id, lineItem.quantity - 1)
      );

      const updateRestMiniQuantityRequest = yield take([
        Shop.UPDATE_QUANTITY_RECEIVE,
        Shop.UPDATE_QUANTITY_REQUEST_FAILURE,
      ]);

      if (
        updateRestMiniQuantityRequest.type ===
        Shop.UPDATE_QUANTITY_REQUEST_FAILURE
      ) {
        return Logger.d(
          TAG,
          `Removing free rest error: ${JSON.stringify(
            updateRestMiniQuantityRequest.errors
          )}`
        );
      }

      const updatedCheckout = yield select(getCheckoutState);

      // Then update Shop.giftWithPurchaseProducts
      yield put(
        removeProductAsGiftWithPurchaseReceive(REST_MINI_KEY, updatedCheckout)
      );
      return Logger.d(TAG, "Removed free rest successfully from cart");
    }

    // When Rest line item and there is a Rest Mini
    // in the cart
    if (
      action.type === Shop.REMOVE_FROM_CART_RECEIVE &&
      IS_REST_MINI_PROMO_ENABLED
    ) {
      // console.log('there is rest and there is rest mini in the card');
      if (isRestProduct) {
        yield put(
          removeProductAsGiftWithPurchaseRequest({
            checkoutId: action.checkout.id,
            lineItem: action.lineItemId,
            productKey: REST_MINI_KEY,
          })
        );

        const changedFreeRestMiniToFullPriceRequest = yield take([
          Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
          Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE,
        ]);

        if (
          changedFreeRestMiniToFullPriceRequest.type ===
          Shop.UPDATE_QUANTITY_REQUEST_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Changing free rest mini to full price error: ${JSON.stringify(
              changedFreeRestMiniToFullPriceRequest.errors
            )}`
          );
        }

        return Logger.d(TAG, "Changing free rest mini to full price success!");
      }

      if (isRestMiniProduct) {
        const cartLineItems = yield select(getCartLineItems);
        const restMiniLineItem = getLineItemDataByVariantId(
          cartLineItems,
          restMiniData.variants[0].id
        );
        const restLineItem = getLineItemDataByVariantId(
          cartLineItems,
          restData.variants[0].id
        );
        let restMiniAddable = false;
        if (restLineItem) {
          if (!restMiniLineItem && restLineItem.quantity === 1) {
            restMiniAddable = true;
          } else if (!restMiniLineItem) {
            restMiniAddable = true;
          } else if (
            restMiniLineItem.quantity < 2 &&
            restLineItem.quantity === 2
          ) {
            restMiniAddable = true;
          }
        }
        if (restMiniAddable) {
          const productData = constructCtaProductData(
            restMiniData,
            restMiniData.variantId
          );

          yield put(
            addToCartRequest({
              checkoutId,
              quantity: 1,
              variantId: productData.variantId,
              customAttributes: productData.customAttributes,
            })
          );

          const addFreeRestMiniToCart = yield take([
            Shop.ADD_TO_CART_RECEIVE,
            Shop.ADD_TO_CART_REQUEST_FAILURE,
          ]);

          if (addFreeRestMiniToCart.type === Shop.ADD_TO_CART_REQUEST_FAILURE) {
            return Logger.d(
              TAG,
              `Add free rest to cart error: ${JSON.stringify(
                addFreeRestMiniToCart.errors
              )}`
            );
          }
        }
      }
    }
  }

  function* watcher() {
    yield takeEvery(
      [
        Shop.ADD_TO_CART_RECEIVE,
        Shop.UPDATE_QUANTITY_RECEIVE,
        Shop.REMOVE_FROM_CART_RECEIVE,
        Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
        Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
        Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST,
      ],
      worker
    );
  }

  return {
    watcher,
    worker,
  };
};

export default RestMiniGiftWithPurchaseSaga;
